import { type AnyExtension } from '@tiptap/core'
import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import ListItem from '@tiptap/extension-list-item'
import TaskList from '@tiptap/extension-task-list'
import { TextStyle } from '@tiptap/extension-text-style'
import StarterKit from '@tiptap/starter-kit'

import { FileNode } from './nodes/file.node'
import { ImageNode } from './nodes/image.node'
import { MentionNode } from './nodes/mention.node'
import { TaskItemNode } from './nodes/taskitem.node'
import { VariableNode } from './nodes/variable.node'

/**
 * Shared collection of Tiptap extensions for richtext content.
 *
 * @warning Only server-side safe extensions are allowed.
 *
 * Server extends this collection with the server safe base extensions and custom nodes.
 *
 * Client extends this collection, then extends each custom node to add client-only features
 * like React views (`addNodeView()`).
 */
export const SHARED_RICHTEXT_EXTENSIONS = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle,
  StarterKit as AnyExtension, // Temp: casting needed after moving to ~shared
  Highlight.configure({
    multicolor: true,
    HTMLAttributes: {
      class: 'px-0.5 rounded-xs box-decoration-clone',
    },
  }),
  TaskList,
] as const satisfies AnyExtension[]

/**
 * Server's collection of Tiptap extensions for richtext content.
 *
 * @see {@link SHARED_RICHTEXT_EXTENSIONS} for warning.
 */
export const SERVER_RICHTEXT_EXTENSIONS = [
  // Shared base
  ...SHARED_RICHTEXT_EXTENSIONS,
  // Server-only custom nodes
  ImageNode,
  FileNode,
  MentionNode,
  VariableNode,
  TaskItemNode,
] as const satisfies AnyExtension[]
